import { z } from 'zod';
import { automationCategoryResponseSchema } from './automation-category.js';
import { AutomationTemplateSyncStatusEnum as EngageLibraryJobStatusEnum } from './automation-template.js';
import { BusinessAutomationStatusEnum } from './business-automation.js';
import { TenantSyncStateEnum } from './tenant-filter.js';
export const MAX_ENGAGE_LIBRARY_SYNC_ITEMS = 100;
export var EngageLibraryItemTypeEnum;
(function (EngageLibraryItemTypeEnum) {
    EngageLibraryItemTypeEnum["AUTOMATION"] = "automation";
    EngageLibraryItemTypeEnum["SEGMENT"] = "segment";
})(EngageLibraryItemTypeEnum || (EngageLibraryItemTypeEnum = {}));
export var EngageLibrarySyncActionEnum;
(function (EngageLibrarySyncActionEnum) {
    EngageLibrarySyncActionEnum["SYNC"] = "sync";
    EngageLibrarySyncActionEnum["UNSYNC"] = "unsync";
})(EngageLibrarySyncActionEnum || (EngageLibrarySyncActionEnum = {}));
export var EngageLibrarySyncStatusEnum;
(function (EngageLibrarySyncStatusEnum) {
    EngageLibrarySyncStatusEnum["SYNCED"] = "synced";
    EngageLibrarySyncStatusEnum["QUEUED"] = "queued";
    EngageLibrarySyncStatusEnum["FAILED"] = "failed";
})(EngageLibrarySyncStatusEnum || (EngageLibrarySyncStatusEnum = {}));
export var EngageLibrarySyncFailureReasonEnum;
(function (EngageLibrarySyncFailureReasonEnum) {
    EngageLibrarySyncFailureReasonEnum["SEGMENT_IN_USE"] = "segment_in_use";
    EngageLibrarySyncFailureReasonEnum["SYSTEM_SEGMENT"] = "system_segment";
    EngageLibrarySyncFailureReasonEnum["CONFLICTING_TEMPLATE"] = "conflicting_template";
    EngageLibrarySyncFailureReasonEnum["NOT_FOUND"] = "not_found";
    EngageLibrarySyncFailureReasonEnum["INTERNAL_ERROR"] = "internal_error";
})(EngageLibrarySyncFailureReasonEnum || (EngageLibrarySyncFailureReasonEnum = {}));
export { EngageLibraryJobStatusEnum };
export const engageLibraryAutomationSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    updatedAt: z.string().datetime().nullable(),
    tenantSyncState: z.nativeEnum(TenantSyncStateEnum),
    retryable: z.boolean(),
    businessAutomationStatus: z.nativeEnum(BusinessAutomationStatusEnum).nullable(),
    categories: z.array(automationCategoryResponseSchema),
});
export const engageLibrarySegmentSchema = z.object({
    canonicalVersionId: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    updatedAt: z.string().datetime().nullable(),
    tenantSyncState: z.nativeEnum(TenantSyncStateEnum),
    retryable: z.boolean(),
    /** False for system segments, such as All Users, that every tenant must keep. */
    removable: z.boolean(),
});
export const engageLibraryResponseSchema = z.object({
    automations: z.array(engageLibraryAutomationSchema),
    segments: z.array(engageLibrarySegmentSchema),
});
export const engageLibrarySyncItemSchema = z.object({
    type: z.nativeEnum(EngageLibraryItemTypeEnum),
    id: z.string().uuid(),
});
const engageLibrarySyncItemsSchema = z
    .array(engageLibrarySyncItemSchema)
    .max(MAX_ENGAGE_LIBRARY_SYNC_ITEMS);
export const engageLibrarySyncRequestSchema = z
    .object({
    sync: engageLibrarySyncItemsSchema,
    unsync: engageLibrarySyncItemsSchema,
})
    .superRefine(({ sync, unsync }, context) => {
    const seenItems = new Set();
    for (const [action, items] of [
        [EngageLibrarySyncActionEnum.SYNC, sync],
        [EngageLibrarySyncActionEnum.UNSYNC, unsync],
    ]) {
        for (const item of items) {
            const itemKey = `${item.type}:${item.id}`;
            if (seenItems.has(itemKey)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Each library item may appear only once per request',
                    path: [action],
                });
            }
            seenItems.add(itemKey);
        }
    }
    if (sync.length + unsync.length > MAX_ENGAGE_LIBRARY_SYNC_ITEMS) {
        context.addIssue({
            code: 'too_big',
            maximum: MAX_ENGAGE_LIBRARY_SYNC_ITEMS,
            inclusive: true,
            origin: 'array',
            message: `A request may change at most ${MAX_ENGAGE_LIBRARY_SYNC_ITEMS} items`,
            path: [],
        });
    }
});
const engageLibrarySyncResultBaseSchema = z.object({
    type: z.nativeEnum(EngageLibraryItemTypeEnum),
    id: z.string().uuid(),
    action: z.nativeEnum(EngageLibrarySyncActionEnum),
});
export const engageLibrarySyncResultSchema = z.discriminatedUnion('status', [
    engageLibrarySyncResultBaseSchema.extend({
        status: z.literal(EngageLibrarySyncStatusEnum.SYNCED),
    }),
    engageLibrarySyncResultBaseSchema.extend({
        status: z.literal(EngageLibrarySyncStatusEnum.QUEUED),
    }),
    engageLibrarySyncResultBaseSchema.extend({
        status: z.literal(EngageLibrarySyncStatusEnum.FAILED),
        reason: z.nativeEnum(EngageLibrarySyncFailureReasonEnum),
        conflictingAutomations: z.array(z.string()).optional(),
    }),
]);
export const engageLibrarySyncResponseSchema = z.object({
    results: z.array(engageLibrarySyncResultSchema),
    jobId: z.string().uuid().nullable(),
});
export const engageLibraryJobStatusResponseSchema = z.object({
    status: z.nativeEnum(EngageLibraryJobStatusEnum),
});
//# sourceMappingURL=engage-library.js.map